import { Component, ChangeDetectionStrategy, input, output, signal, computed, effect } from '@angular/core';

import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-tags-display',
  standalone: true,
  imports: [
    ChipModule,
    TooltipModule,
    TranslateModule
  ],
  templateUrl: './tags-display.component.html',
  styleUrls: ['./tags-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagsDisplayComponent {
  readonly tags = input<string[] | undefined>();
  readonly compact = input(false);
  readonly clickable = input(true);
  readonly maxVisible = input(8);

  readonly tagClick = output<string>();

  private _tags = signal<string[]>([]);
  private _expanded = signal(false);

  constructor() {
    effect(() => {
      this._tags.set(this.tags() || []);
    });
  }

  expanded = computed(() => this._expanded());

  visibleTags = computed(() => {
    const allTags = this._tags();
    const max = this.maxVisible();

    if (this.expanded() || allTags.length <= max) {
      return allTags;
    }

    return allTags.slice(0, max);
  });

  hasMore = computed(() => {
    const allTags = this._tags();
    const max = this.maxVisible();
    return allTags.length > max;
  });

  remainingCount = computed(() => {
    const allTags = this._tags();
    const visible = this.visibleTags();
    return allTags.length - visible.length;
  });

  // Public getter methods for template
  getTags(): string[] {
    return this._tags();
  }

  isCompact(): boolean {
    return this.compact();
  }

  isClickable(): boolean {
    return this.clickable();
  }

  onTagClick(tag: string) {
    // Gated on the SAME predicate as the chip's stop and role — a plain label acts on no click.
    if (this.isClickable()) {
      this.tagClick.emit(tag);
    }
  }

  toggleExpanded() {
    this._expanded.set(!this._expanded());
  }
}
